import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { usePermissions } from "../contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { toast } from "sonner";
import { X, Trash2, Printer, Package, MapPin, Truck, Pencil, Minus, Plus, Lock, Camera, Image, AlertTriangle, CheckCircle, Star, ThumbsUp, ThumbsDown, Copy, MessageSquare, Send, RotateCcw } from "lucide-react";

// Kvalitetsvurdering options
const QUALITY_OPTIONS = [
  { value: 'good', label: 'God', emoji: '👍', color: 'green', description: 'Godt sorteret' },
  { value: 'medium', label: 'Acceptabel', emoji: '👌', color: 'yellow', description: 'Nogenlunde sorteret' },
  { value: 'poor', label: 'Dårlig', emoji: '👎', color: 'red', description: 'Dårligt sorteret' }
];

const STATUS_CONFIG = {
  'grøn': { color: 'bg-green-500', text: 'OK / Tømt', textColor: 'text-green-400' },
  'gul': { color: 'bg-yellow-500', text: 'Tømning på vej', textColor: 'text-yellow-400' },
  'rød': { color: 'bg-red-500', text: 'Afventer tømning', textColor: 'text-red-400' }
};

// Status konfiguration for opbygning/nedpakning
const SETUP_STATUS_CONFIG = {
  'red': { color: 'bg-red-500', text: 'Ikke opsat', textColor: 'text-red-400' },
  'yellow': { color: 'bg-yellow-500', text: 'Delvist opsat', textColor: 'text-yellow-400' },
  'green': { color: 'bg-green-500', text: 'Fuldt opsat', textColor: 'text-green-400' }
};

const TEARDOWN_STATUS_CONFIG = {
  'red': { color: 'bg-red-500', text: 'Ikke hjemtaget', textColor: 'text-red-400' },
  'yellow': { color: 'bg-yellow-500', text: 'Delvist hjemtaget', textColor: 'text-yellow-400' },
  'green': { color: 'bg-green-500', text: 'Fuldt hjemtaget', textColor: 'text-green-400' }
};

const POINT_TYPE_LABELS = {
  'ressourceø': 'Ressourceø',
  'ressourcespand': 'Ressourcespand',
  'toilet': 'Toilet',
  'pissoir': 'Pissoir',
  'opmærksomhedspunkt': 'Opmærksomhedspunkt',
  'drikkevand': 'Drikkevand',
  'pausetelt': 'Pausetelt',
  'snack': 'Snack',
  'kaffe_te': 'Kaffe & te',
  'food': 'Mad-bod',
  'drinks': 'Drikkevarer-bod',
  'evakueringspunkt': 'Evakueringspunkt'
};

// Ressourcespand fraktioner
const RESOURCE_BIN_FRACTIONS = {
  'krus': 'Krus',
  'rest': 'Rest',
  'bio': 'Bio',
  'plast': 'Plast',
  'stativ': 'Stativ',
  'shotsrør': 'Shotsrør'
};

// ========== OPMÆRKSOMHEDSPUNKT SEKTION ==========
function AttentionPointSection({ point, onUpdate, onDelete, token }) {
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState(point.image_url || null);
  const [description, setDescription] = useState(point.description || '');
  const [isEditing, setIsEditing] = useState(false);
  const [attentionStatus, setAttentionStatus] = useState(point.attention_status || 'new');
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(
        `${API}/resource-points/${point.id}/upload-image`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      
      // Hent opdateret punkt
      const pointRes = await axios.get(`${API}/resource-points/${point.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setImageUrl(pointRes.data.image_url);
      if (onUpdate) onUpdate(pointRes.data);
      toast.success('Billede uploadet');
    } catch (error) {
      console.error('Upload fejl:', error);
      toast.error('Kunne ikke uploade billede');
    }
    setUploading(false);
  };

  const handleSaveDescription = async () => {
    try {
      await axios.put(
        `${API}/resource-points/${point.id}`,
        { description },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onUpdate) {
        onUpdate({ ...point, description });
      }
      setIsEditing(false);
      toast.success('Beskrivelse gemt');
    } catch (error) {
      toast.error('Kunne ikke gemme beskrivelse');
    }
  };

  const handleDeleteImage = async () => {
    try {
      await axios.put(
        `${API}/resource-points/${point.id}`,
        { image_url: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setImageUrl(null);
      if (onUpdate) onUpdate({ ...point, image_url: null });
      toast.success('Billede slettet');
    } catch (error) {
      toast.error('Kunne ikke slette billede');
    }
  };

  // Sæt status til "på vej"
  const handleOnTheWay = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/resource-points/${point.id}`,
        { attention_status: 'on_the_way' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAttentionStatus('on_the_way');
      if (onUpdate) onUpdate({ ...point, attention_status: 'on_the_way' });
      toast.success('Markeret som "på vej"');
    } catch (error) {
      toast.error('Kunne ikke opdatere status');
    }
    setLoading(false);
  };

  // Marker som løst og slet punktet
  const handleResolved = async () => {
    setLoading(true);
    try {
      // Slet punktet
      await axios.delete(
        `${API}/resource-points/${point.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Opmærksomhedspunkt løst og fjernet');
      if (onDelete) onDelete(point.id);
    } catch (error) {
      toast.error('Kunne ikke fjerne punktet');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-4">
      {/* Status knapper */}
      <div className="space-y-2">
        <h4 className="text-sm font-bold text-zinc-300 uppercase tracking-wide">Status</h4>
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            className={`flex-col h-auto py-4 ${
              attentionStatus === 'on_the_way' 
                ? 'bg-yellow-500/30 border-yellow-500 text-yellow-300' 
                : 'bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20 text-yellow-400'
            }`}
            onClick={handleOnTheWay}
            disabled={loading || attentionStatus === 'on_the_way'}
          >
            <Truck className="h-6 w-6 mb-2" />
            <span className="text-sm font-medium">Er på vej</span>
          </Button>
          <Button
            variant="outline"
            className="flex-col h-auto py-4 bg-green-500/10 border-green-500/30 hover:bg-green-500/20 text-green-400"
            onClick={handleResolved}
            disabled={loading}
          >
            <CheckCircle className="h-6 w-6 mb-2" />
            <span className="text-sm font-medium">Er løst</span>
          </Button>
        </div>
        {attentionStatus === 'on_the_way' && (
          <p className="text-xs text-yellow-400 text-center">Nogen er på vej til at løse problemet</p>
        )}
      </div>

      {/* Beskrivelse */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-bold text-zinc-300 uppercase tracking-wide">Beskrivelse</h4>
          {!isEditing && (
            <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)} className="h-7">
              <Pencil className="h-3 w-3 mr-1" />Rediger
            </Button>
          )}
        </div>
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Beskriv opmærksomhedspunktet (f.eks. hul i hegn, ødelagt lys, palle der skal hentes...)"
              className="w-full bg-zinc-800 border border-zinc-600 rounded-lg p-3 text-sm min-h-[100px] resize-none"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSaveDescription}>Gem</Button>
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>Annuller</Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-zinc-400 bg-zinc-800/50 rounded-lg p-3">
            {description || 'Ingen beskrivelse tilføjet.'}
          </p>
        )}
      </div>

      {/* Billede sektion */}
      <div className="space-y-2">
        <h4 className="text-sm font-bold text-zinc-300 uppercase tracking-wide">Billede</h4>
        
        {imageUrl ? (
          <div className="relative">
            <img 
              src={imageUrl} 
              alt="Opmærksomhedspunkt billede" 
              className="w-full rounded-lg border border-zinc-700 max-h-64 object-cover"
            />
            <div className="absolute top-2 right-2 flex gap-1">
              <Button 
                size="sm" 
                variant="secondary" 
                onClick={() => fileInputRef.current?.click()}
                className="h-8 bg-black/50 hover:bg-black/70"
              >
                <Camera className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="destructive" 
                onClick={handleDeleteImage}
                className="h-8"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full border-2 border-dashed border-zinc-600 rounded-lg p-8 flex flex-col items-center gap-2 hover:border-primary/50 hover:bg-zinc-800/30 transition-colors"
          >
            {uploading ? (
              <span className="text-sm text-zinc-400">Uploader...</span>
            ) : (
              <>
                <Camera className="h-8 w-8 text-zinc-500" />
                <span className="text-sm text-zinc-400">Klik for at uploade billede</span>
                <span className="text-xs text-zinc-500">eller tag et foto med kameraet</span>
              </>
            )}
          </button>
        )}
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleImageUpload}
          className="hidden"
        />
      </div>
    </div>
  );
}

// ========== TØMNINGS-DIALOG MED ANTAL OG KVALITETSVURDERING ==========
function EmptyingDialog({ 
  open, 
  onClose, 
  container, 
  containerIndex, 
  showQuality, 
  onConfirm, 
  direct = false,
  loading 
}) {
  const [count, setCount] = useState(1);
  const [quality, setQuality] = useState('good');
  const maxCount = container?.count || 1;
  
  // Reset når dialog åbnes
  useEffect(() => {
    if (open) {
      setCount(1);
      setQuality('good');
    }
  }, [open]);
  
  if (!open || !container) return null;
  
  return (
    <div 
      className="fixed inset-0 z-[3000] flex items-center justify-center p-4 bg-black/80"
      onClick={onClose}
    >
      <div 
        className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="emptying-dialog"
      >
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Truck className="h-5 w-5 text-green-400" />
          {direct ? 'Registrér som tømt nu' : 'Marker som tømt'}
        </h3>
        {direct && (
          <p className="text-xs text-emerald-400 -mt-2 mb-4">
            Tømningen registreres direkte uden bestillingsflow — tælles med i statistikken.
          </p>
        )}
        
        {/* Container info */}
        <div className="bg-zinc-800 rounded-lg p-3 mb-4">
          <p className="font-bold text-white">{container.container_size}</p>
          <p className="text-sm text-white font-mono font-semibold">{container.fraction_type}</p>
          <p className="text-xs text-zinc-500 mt-1">Total: {maxCount} stk.</p>
        </div>
        
        {/* Antal vælger */}
        <div className="mb-4">
          <label className="text-sm font-bold text-zinc-300 block mb-2">
            Hvor mange blev tømt?
          </label>
          <div className="flex items-center justify-center gap-4 bg-zinc-800/50 rounded-lg p-3">
            <Button
              variant="outline"
              size="sm"
              className="h-10 w-10 p-0 bg-zinc-800 border-zinc-600 hover:bg-zinc-700"
              onClick={() => setCount(Math.max(1, count - 1))}
              disabled={count <= 1}
              data-testid="emptying-count-minus"
            >
              <Minus className="h-5 w-5" />
            </Button>
            <div className="text-center">
              <span className="text-3xl font-bold text-white" data-testid="emptying-count-value">
                {count}
              </span>
              <span className="text-lg text-zinc-400">/{maxCount}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-10 w-10 p-0 bg-zinc-800 border-zinc-600 hover:bg-zinc-700"
              onClick={() => setCount(Math.min(maxCount, count + 1))}
              disabled={count >= maxCount}
              data-testid="emptying-count-plus"
            >
              <Plus className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        {/* Kvalitetsvurdering - kun synlig i afvikling */}
        {showQuality && (
          <div className="mb-4">
            <label className="text-sm font-bold text-zinc-300 block mb-2">
              Sorteringskvalitet
            </label>
            <div className="grid grid-cols-3 gap-2">
              {QUALITY_OPTIONS.map(opt => (
                <Button
                  key={opt.value}
                  variant="outline"
                  className={`flex-col h-auto py-3 ${
                    quality === opt.value
                      ? `bg-${opt.color}-500/30 border-${opt.color}-500`
                      : `bg-${opt.color}-500/10 border-${opt.color}-500/30 hover:bg-${opt.color}-500/20`
                  }`}
                  onClick={() => setQuality(opt.value)}
                  data-testid={`quality-${opt.value}`}
                >
                  <span className="text-2xl mb-1">{opt.emoji}</span>
                  <span className={`text-xs text-${opt.color}-400`}>{opt.label}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
        
        {/* Action buttons */}
        <div className="flex gap-2 mt-6">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={loading}
          >
            Annuller
          </Button>
          <Button
            className="flex-1 bg-green-600 hover:bg-green-700"
            onClick={() => onConfirm(containerIndex, count, quality)}
            disabled={loading}
            data-testid="confirm-emptying-btn"
          >
            {loading ? 'Gemmer...' : `Markér ${count} som tømt`}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PointDetailDialog({ point: initialPoint, open, onClose, onUpdate, onDelete, onPrintQR, onEdit, onCopy, currentPhase, fractionOrder, binFractionOrder, attentionMarkers = [] }) {
  const [loading, setLoading] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState(null);
  const [selectedBin, setSelectedBin] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [pendingContainers, setPendingContainers] = useState([]);
  // Ny state for tømnings-dialog
  const [emptyingDialog, setEmptyingDialog] = useState({ open: false, container: null, containerIndex: null, source: 'containers' });
  const [hasPartialEmptying, setHasPartialEmptying] = useState(false);
  const { canPerformAction, isAdmin } = usePermissions();
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [point, setPoint] = useState(initialPoint);
  // Lokal state for antal-ændringer (gemmes først ved lukning)
  const [localChecklist, setLocalChecklist] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const token = localStorage.getItem("token");
  
  // Notes state for food/drinks/ressourceø
  const [pointNotes, setPointNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);
  
  const canSeeNotes = isAdmin || canPerformAction('kan_se_food_drinks_noter');
  const showNotesSection = canSeeNotes && point && ['food', 'drinks', 'ressourceø'].includes(point.point_type);
  
  // Load notes when point changes
  useEffect(() => {
    if (open && point?.id && showNotesSection) {
      loadPointNotes();
    }
  }, [open, point?.id, showNotesSection]);
  
  const loadPointNotes = async () => {
    if (!point?.id) return;
    setNotesLoading(true);
    try {
      const eventId = localStorage.getItem("selectedEventId");
      const res = await axios.get(`${API}/events/${eventId}/point-notes/${point.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPointNotes(res.data || []);
    } catch (err) {
      console.error("Error loading notes:", err);
    } finally {
      setNotesLoading(false);
    }
  };
  
  const handleAddNote = async () => {
    if (!newNoteText.trim() || !point?.id) return;
    try {
      const eventId = localStorage.getItem("selectedEventId");
      await axios.post(`${API}/events/${eventId}/point-notes/${point.id}`, 
        { content: newNoteText },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setNewNoteText('');
      loadPointNotes();
      toast.success("Note tilføjet");
    } catch (err) {
      toast.error("Fejl ved tilføjelse af note");
    }
  };
  
  const handleDeleteNote = async (noteId) => {
    try {
      const eventId = localStorage.getItem("selectedEventId");
      await axios.delete(`${API}/events/${eventId}/point-notes/${point.id}/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadPointNotes();
      toast.success("Note slettet");
    } catch (err) {
      toast.error("Fejl ved sletning");
    }
  };
  
  // Opdater lokal point og nulstil ændringer når prop ændres
  useEffect(() => {
    // Sortér containere efter stamdata-rækkefølge
    if (initialPoint?.containers?.length && fractionOrder?.length) {
      const sorted = [...initialPoint.containers].sort((a, b) => {
        const idxA = fractionOrder.indexOf(a.fraction_type);
        const idxB = fractionOrder.indexOf(b.fraction_type);
        return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
      });
      // Sortér bins efter stamdata-rækkefølge
      let sortedBins = initialPoint.bins;
      if (initialPoint.bins?.length && binFractionOrder?.length) {
        const binOrder = binFractionOrder.map(f => f.value);
        sortedBins = [...initialPoint.bins].sort((a, b) => {
          const idxA = binOrder.indexOf(a.fraction_type);
          const idxB = binOrder.indexOf(b.fraction_type);
          return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
        });
      }
      setPoint({ ...initialPoint, containers: sorted, ...(sortedBins ? { bins: sortedBins } : {}) });
    } else if (initialPoint?.bins?.length && binFractionOrder?.length) {
      const binOrder = binFractionOrder.map(f => f.value);
      const sortedBins = [...initialPoint.bins].sort((a, b) => {
        const idxA = binOrder.indexOf(a.fraction_type);
        const idxB = binOrder.indexOf(b.fraction_type);
        return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
      });
      setPoint({ ...initialPoint, bins: sortedBins });
    } else {
      setPoint(initialPoint);
    }
    // Initialiser localChecklist med ALLE items (ikke kun dem der allerede er i checklist)
    const initialChecklist = {};
    
    if (initialPoint) {
      // Hent eksisterende checklist-værdier.
      // I NEDTAGNING bruges en SEPARAT tjekliste (teardown_checklist), så "hjemtaget"
      // starter på 0 og ikke arver opbygnings-afkrydsningerne (som ellers fik alt til
      // at se hjemtaget ud).
      const existingChecklist = {};
      const sourceChecklist = currentPhase === 'teardown'
        ? initialPoint.teardown_checklist
        : initialPoint.setup_checklist;
      if (sourceChecklist) {
        sourceChecklist.forEach(item => {
          existingChecklist[item.item_id] = item.checked_count ?? (item.checked ? item.total_count || 1 : 0);
        });
      }
      
      // Tilføj alle containere (for ressourceø)
      if (initialPoint.point_type === 'ressourceø' && initialPoint.containers) {
        initialPoint.containers.forEach((c, idx) => {
          const id = `${c.container_size}_${c.fraction_type}_${idx}`;
          initialChecklist[id] = existingChecklist[id] ?? 0;
        });
      }
      
      // Tilføj alle bins (for ressourcespand)
      if (initialPoint.point_type === 'ressourcespand') {
        if (initialPoint.bins && initialPoint.bins.length > 0) {
          initialPoint.bins.forEach((b, idx) => {
            const id = `bin_${b.fraction_type}_${idx}`;
            initialChecklist[id] = existingChecklist[id] ?? 0;
          });
        } else if (initialPoint.bin_fraction) {
          const id = `bin_${initialPoint.bin_fraction}_0`;
          initialChecklist[id] = existingChecklist[id] ?? 0;
        }
      }
    }
    
    setLocalChecklist(initialChecklist);
    setHasChanges(false);
  }, [initialPoint, fractionOrder, binFractionOrder, currentPhase]);
  
  // Er vi i opbygning/nedpakning mode?
  // "Sæt i drift": en operationel ø behandles som AFVIKLING i hele dialogen
  // (viser tømnings-UI, skjuler opbygnings-tjekliste) selvom eventet er i opbygning/nedtagning.
  const realIsSetupOrTeardown = ['setup', 'teardown'].includes(currentPhase);
  const effectivePhase = point?.operational ? 'operation' : currentPhase;
  const isSetupMode = effectivePhase === 'setup';
  const isTeardownMode = effectivePhase === 'teardown';
  const isSetupOrTeardown = isSetupMode || isTeardownMode;

  // Hent brugerens rolle og ventende containere
  useEffect(() => {
    if (open && token) {
      // Hent brugerinfo
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setUserRole(res.data.role);
      }).catch(() => {
        setUserRole(null);
      });

      // Hent ventende anmodninger for dette punkt
      if (point?.id) {
        axios.get(`${API}/resource-points/${point.id}/pending-requests`)
          .then(res => {
            // Saml alle containere fra ventende anmodninger
            const containers = [];
            res.data.forEach(req => {
              (req.containers || []).forEach(c => {
                containers.push(c);
              });
            });
            setPendingContainers(containers);
          })
          .catch(() => setPendingContainers([]));
      }
    }
  }, [open, token, point?.id]);

  if (!open || !point) return null;

  const isDriver = userRole === 'chauffør';
  const isHeartLeader = userRole === 'heart_leader';
  // RBAC: Brug permissions hook i stedet for hardcoded roller.
  // Granulær pr. punkttype - matcher backend's require_permission.
  const pt = initialPoint?.point_type;
  const pt_edit_key = {
    'ressourceø': 'ressourceoe_rediger',
    'ressourcespand': 'ressourcespand_rediger',
    'opmærksomhedspunkt': 'opmaerkningspunkt_rediger',
    'drikkevand': 'spec_punkt_rediger',
    'pausetelt': 'spec_punkt_rediger',
    'snack': 'spec_punkt_rediger',
    'kaffe_te': 'spec_punkt_rediger',
    'toilet': 'spec_punkt_rediger',
    'pissoir': 'spec_punkt_rediger',
    'food': 'food_drinks_rediger',
    'drinks': 'food_drinks_rediger',
    'maxicontainer': 'maxicontainer_rediger',
  }[pt] || 'ressource_rediger';
  const pt_delete_key = {
    'ressourceø': 'ressourceoe_slet',
    'ressourcespand': 'ressourcespand_slet',
    'opmærksomhedspunkt': 'opmaerkningspunkt_slet',
    'drikkevand': 'spec_punkt_slet',
    'pausetelt': 'spec_punkt_slet',
    'snack': 'spec_punkt_slet',
    'kaffe_te': 'spec_punkt_slet',
    'toilet': 'spec_punkt_slet',
    'pissoir': 'spec_punkt_slet',
    'food': 'food_drinks_slet',
    'drinks': 'food_drinks_slet',
    'maxicontainer': 'maxicontainer_slet',
  }[pt] || 'ressource_slet';
  const canDelete = isAdmin || canPerformAction(pt_delete_key);
  const canEdit = isAdmin || canPerformAction(pt_edit_key);
  
  // RBAC: Granulære rettigheder for ressourceøer
  const canChangeStatusRessourceoer = isAdmin || canPerformAction('kan_aendre_status_ressourceoer');
  const canOrderEmptyingRessourceoer = isAdmin || canPerformAction('kan_bestille_tomning_ressourceoer');
  
  // RBAC: Granulære rettigheder for ressourcespande
  const canChangeStatusRessourcespande = isAdmin || canPerformAction('kan_aendre_status_ressourcespande');
  const canOrderEmptyingRessourcespande = isAdmin || canPerformAction('kan_bestille_tomning_ressourcespande');
  
  // RBAC: Granulære rettigheder for toiletter - FJERNET i Fase 3 (simple punkter nu)
  // const canChangeStatusToiletter = isAdmin || canPerformAction('kan_aendre_status_toiletter');
  // const canOrderCleaningToiletter = isAdmin || canPerformAction('kan_bestille_rengoring_toiletter');
  
  // RBAC: Granulære rettigheder for pissoir - FJERNET i Fase 3 (simple punkter nu)
  // const canChangeStatusPissoir = isAdmin || canPerformAction('kan_aendre_status_pissoir');
  // const canOrderCleaningPissoir = isAdmin || canPerformAction('kan_bestille_rengoring_pissoir');
  
  // RBAC: Granulære rettigheder for maxicontainere
  const canChangeStatusMaxi = isAdmin || canPerformAction('kan_aendre_status_maxicontainere');
  const canOrderEmptyingMaxi = isAdmin || canPerformAction('kan_bestille_tomning_maxicontainere');
  
  // Hjælpefunktion til at tjekke om bruger kan ændre status for dette punkt-type
  const canChangeStatusForPointType = () => {
    switch (point?.point_type) {
      case 'ressourceø': return canChangeStatusRessourceoer;
      case 'ressourcespand': return canChangeStatusRessourcespande;
      case 'toilet': return false; // Simpelt punkt - ingen status
      case 'pissoir': return false; // Simpelt punkt - ingen status
      case 'food': return false; // Food trucks har ingen status
      case 'drinks': return false; // Drinks trucks har ingen status
      case 'maxicontainer': return canChangeStatusMaxi;
      case 'opmærksomhedspunkt': return true; // Alle kan ændre opmærksomhedspunkter
      default: return isAdmin;
    }
  };
  
  // Hjælpefunktion til at tjekke om bruger kan bestille tømning/rengøring
  const canOrderForPointType = () => {
    switch (point.point_type) {
      case 'ressourceø': return canOrderEmptyingRessourceoer;
      case 'ressourcespand': return canOrderEmptyingRessourcespande;
      case 'toilet': return false; // Simpelt punkt - ingen bestilling
      case 'pissoir': return false; // Simpelt punkt - ingen bestilling
      case 'food': return false; // Food trucks - ingen bestilling
      case 'drinks': return false; // Drinks trucks - ingen bestilling
      case 'maxicontainer': return canOrderEmptyingMaxi;
      default: return isAdmin;
    }
  };

  // "Sæt i drift" / "Tag ud af drift" (kun admin) — gør at en ø kan tømmes
  // selvom eventet stadig er i opbygning/nedtagning.
  const toggleOperational = async () => {
    setLoading(true);
    try {
      const newVal = !point.operational;
      await axios.put(
        `${API}/resource-points/${point.id}/operational`,
        { operational: newVal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updated = { ...point, operational: newVal };
      setPoint(updated);
      onUpdate && onUpdate(updated);
      toast.success(newVal ? 'Ø sat i drift – tømning kan nu bestilles' : 'Ø taget ud af drift');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fejl ved ændring af drift-status');
    } finally {
      setLoading(false);
    }
  };


  // Beregn lokal setup status baseret på localChecklist
  const calculateLocalSetupStatus = () => {
    let totalItems = 0;
    let checkedItems = 0;
    
    if (point.point_type === 'ressourceø') {
      point.containers?.forEach((c, idx) => {
        const id = `${c.container_size}_${c.fraction_type}_${idx}`;
        const count = c.count || 1;
        totalItems += count;
        checkedItems += localChecklist[id] ?? 0;
      });
    } else if (point.point_type === 'ressourcespand') {
      if (point.bins?.length > 0) {
        point.bins.forEach((b, idx) => {
          const id = `bin_${b.fraction_type}_${idx}`;
          totalItems += b.count || 1;
          checkedItems += localChecklist[id] ?? 0;
        });
      } else if (point.bin_fraction) {
        const id = `bin_${point.bin_fraction}_0`;
        totalItems = 1;
        checkedItems = localChecklist[id] ?? 0;
      }
    }
    
    if (totalItems === 0) return point.setup_status || 'red';
    if (checkedItems === 0) return 'red';
    if (checkedItems >= totalItems) return 'green';
    return 'yellow';
  };
  
  const localSetupStatus = calculateLocalSetupStatus();

  // Find status for en specifik container
  const getContainerStatus = (container) => {
    const isPending = pendingContainers.some(pc => 
      pc.container_size === container.container_size && 
      pc.fraction_type === container.fraction_type
    );
    // Hvis containeren er pending OG punktet er 'gul' (på vej), vis 'gul'
    if (isPending && point.status === 'gul') return 'gul';
    if (isPending) return 'rød'; // Venter på tømning
    return 'grøn'; // OK
  };

  // Find status for en specifik bin (samme logik som container)
  const getBinStatus = (bin) => {
    const isPending = pendingContainers.some(pc =>
      (pc.container_size || 'Spand') === (bin.container_size || 'Spand') &&
      pc.fraction_type === bin.fraction_type
    );
    if (isPending && point.status === 'gul') return 'gul';
    if (isPending) return 'rød';
    return 'grøn';
  };

  // Ændre status for hele punktet (bruges af de store knapper)
  const handlePointStatusChange = async (newStatus) => {
    setLoading(true);
    try {
      await axios.put(`${API}/resource-points/${point.id}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      // Hvis vi bestiller tømning for hele punktet
      if (newStatus === 'rød') {
        await axios.post(`${API}/emptying-requests`, {
          point_id: point.id,
          request_type: point.point_type,
          containers: point.containers || [],
          source: 'manual'
        });
      }

      const statusText = STATUS_CONFIG[newStatus].text;
      toast.success(`Status opdateret: ${statusText}`);
      onUpdate?.();
      onClose();
    } catch (error) {
      toast.error("Fejl ved opdatering af status");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Ændre status for en specifik container
  const handleContainerStatusChange = async (containerIndex, newStatus) => {
    const container = point.containers[containerIndex];
    
    if (newStatus === 'grøn') {
      // Åbn tømnings-dialogen med antal-vælger
      setEmptyingDialog({
        open: true,
        container: container,
        containerIndex: containerIndex
      });
      return; // Vent på dialog-bekræftelse
    }
    
    setLoading(true);
    try {
      if (newStatus === 'rød') {
        // Bestil tømning for denne container
        await axios.post(`${API}/emptying-requests`, {
          point_id: point.id,
          request_type: point.point_type,
          containers: [container],
          source: 'manual'
        });
        toast.success(`Tømning bestilt for ${container.container_size}`);
      } else if (newStatus === 'gul') {
        // Sæt punkt til "på vej" (dette påvirker hele punktet)
        await axios.put(`${API}/resource-points/${point.id}`, 
          { status: 'gul' },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        // Opdater alle ventende tømningsanmodninger til in_progress
        await axios.post(`${API}/resource-points/${point.id}/mark-in-progress`).catch(() => {});
        toast.success("Tømning er på vej");
        setPoint(prev => ({ ...prev, status: 'gul' }));
      }

      onUpdate?.();
      // Genindlæs pendingContainers
      const pendingRes = await axios.get(`${API}/resource-points/${point.id}/pending-requests`);
      const containers = [];
      pendingRes.data.forEach(req => {
        (req.containers || []).forEach(c => {
          containers.push(c);
        });
      });
      setPendingContainers(containers);
      
    } catch (error) {
      toast.error("Fejl ved opdatering");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  // Bekræft tømning med antal og kvalitet
  const confirmEmptying = async (containerIndex, count, quality) => {
    setLoading(true);
    try {
      const source = emptyingDialog.source || 'containers';
      const container = source === 'bins' ? point.bins[containerIndex] : point.containers[containerIndex];
      
      // Send til API med antal og kvalitet
      const response = await axios.post(`${API}/resource-points/${point.id}/complete-containers`, {
        container_indices: [containerIndex],
        container_details: [{
          container_index: containerIndex,
          count: count,
          sorting_quality: quality
        }],
        source: source
      });
      
      const label = container.container_size || 'Spand';
      toast.success(`${count}x ${label} markeret som tømt`);
      
      // Luk dialogen
      setEmptyingDialog({ open: false, container: null, containerIndex: null, source: 'containers' });
      
      // Tjek om alle containere nu er tømt (status blev grøn)
      if (response.data.new_status === 'grøn') {
        toast.success(source === 'bins' ? "Alle spande er tømt! Punktet er nu grønt." : "Alle containere er tømt! Ressourceøen er nu grøn.");
        setHasPartialEmptying(false);
        onUpdate?.();
        onClose();
        return;
      }

      // Delvis tømning — marker så handleClose ved det
      setHasPartialEmptying(true);
      onUpdate?.();
      // Genindlæs pendingContainers
      const pendingRes = await axios.get(`${API}/resource-points/${point.id}/pending-requests`);
      const containers = [];
      pendingRes.data.forEach(req => {
        (req.containers || []).forEach(c => {
          containers.push(c);
        });
      });
      setPendingContainers(containers);
      
    } catch (error) {
      toast.error("Fejl ved markering af tømning");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Registrér tømning direkte (uden bestillingsflow) — logges kun til statistik
  const confirmDirectEmpty = async (containerIndex, count, quality) => {
    setLoading(true);
    try {
      const source = emptyingDialog.source || 'containers';
      const container = source === 'bins' ? point.bins[containerIndex] : point.containers[containerIndex];
      await axios.post(`${API}/resource-points/${point.id}/direct-empty`, {
        container_indices: [containerIndex],
        container_details: [{
          container_index: containerIndex,
          count: count,
          sorting_quality: quality
        }],
        source: source
      });
      const label = container.container_size || 'Spand';
      toast.success(`${count}x ${label} registreret som tømt`);
      setEmptyingDialog({ open: false, container: null, containerIndex: null, direct: false, source: 'containers' });
      onUpdate?.();
    } catch (error) {
      toast.error("Fejl ved registrering af tømning");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // ===== BIN HANDLERS (samme flow som containere, men for ressourcespand) =====
  const handleBinStatusChange = async (binIndex, newStatus) => {
    const bin = point.bins[binIndex];

    if (newStatus === 'grøn') {
      setEmptyingDialog({
        open: true,
        container: bin,
        containerIndex: binIndex,
        source: 'bins'
      });
      return;
    }

    setLoading(true);
    try {
      if (newStatus === 'rød') {
        await axios.post(`${API}/emptying-requests`, {
          point_id: point.id,
          request_type: point.point_type,
          containers: [bin],
          source: 'manual'
        });
        toast.success(`Tømning bestilt for ${bin.container_size || 'Spand'}`);
      } else if (newStatus === 'gul') {
        await axios.put(`${API}/resource-points/${point.id}`,
          { status: 'gul' },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        await axios.post(`${API}/resource-points/${point.id}/mark-in-progress`).catch(() => {});
        toast.success("Tømning er på vej");
        setPoint(prev => ({ ...prev, status: 'gul' }));
      }
      onUpdate?.();
      const pendingRes = await axios.get(`${API}/resource-points/${point.id}/pending-requests`);
      const containers = [];
      pendingRes.data.forEach(req => {
        (req.containers || []).forEach(c => containers.push(c));
      });
      setPendingContainers(containers);
    } catch (error) {
      toast.error("Fejl ved opdatering");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const showConfirm = (title, message, onConfirm) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
  };

  const handleDelete = () => {
    showConfirm(
      "Slet ressourcepunkt?",
      `Er du sikker på du vil slette "${point.name}"? Denne handling kan ikke fortrydes.`,
      async () => {
        setLoading(true);
        closeConfirm();
        try {
          await axios.delete(`${API}/resource-points/${point.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Punkt slettet");
          onDelete?.();
          onClose();
        } catch (error) {
          toast.error("Fejl ved sletning: " + (error.response?.data?.detail || error.message));
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const currentStatus = STATUS_CONFIG[point.status] || STATUS_CONFIG['grøn'];
  const hasPendingRequests = pendingContainers.length > 0;
  
  // Beregn setup/teardown status - brug lokalSetupStatus for live-opdatering
  const setupStatus = localSetupStatus;
  const getSetupStatusConfig = () => {
    if (isSetupMode) {
      return SETUP_STATUS_CONFIG[setupStatus] || SETUP_STATUS_CONFIG['red'];
    } else if (isTeardownMode) {
      return TEARDOWN_STATUS_CONFIG[setupStatus] || TEARDOWN_STATUS_CONFIG['red'];
    }
    return null;
  };
  const setupStatusConfig = getSetupStatusConfig();

  // Tjek om en container er i ventende anmodninger
  const isContainerPending = (container) => {
    return pendingContainers.some(pc => 
      pc.container_size === container.container_size && 
      pc.fraction_type === container.fraction_type
    );
  };
  
  // Håndter ændring af antal - kun lokal (gemmes ved lukning)
  const handleSetupCheck = (containerId, count, totalCount) => {
    setLocalChecklist(prev => ({
      ...prev,
      [containerId]: Math.max(0, Math.min(count, totalCount))
    }));
    setHasChanges(true);
  };
  
  // Gem alle ændringer til API
  const saveChanges = async () => {
    if (!hasChanges) return;
    
    setLoading(true);
    try {
      const eventId = point.event_id;
      
      // Send alle ændringer til API
      for (const [itemId, count] of Object.entries(localChecklist)) {
        await axios.post(
          `${API}/events/${eventId}/resource-points/${point.id}/setup-check?item_id=${encodeURIComponent(itemId)}&count=${count}`, 
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success(isSetupMode ? 'Opsætning gemt' : 'Nedpakning gemt');
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke gemme ændringer');
    } finally {
      setLoading(false);
    }
  };
  
  // Håndter lukning - gem ændringer først
  const handleClose = async () => {
    if (hasChanges) {
      await saveChanges();
    }
    // Kun reset til rød hvis der er sket en delvis tømning (punkt 5 i flowet)
    if (point?.id && hasPartialEmptying) {
      try {
        await axios.post(`${API}/resource-points/${point.id}/close-emptying-session`);
        onUpdate?.();
      } catch (e) { console.error("Close session error:", e); }
    }
    setHasPartialEmptying(false);
    onClose();
  };
  
  // Håndter afkrydsning af hele punktet (ressourcespand)
  const handlePointSetupCheck = async (newStatus) => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/resource-points/${point.id}/setup-status?status=${newStatus}`, 
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(newStatus === 'green' ? 
        (isSetupMode ? 'Markeret som opsat' : 'Markeret som hjemtaget') :
        newStatus === 'yellow' ? 'Markeret som i gang' : 'Markeret som ikke startet'
      );
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke opdatere status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
      />
      {/* Tømnings-dialog med antal og kvalitet */}
      <EmptyingDialog
        open={emptyingDialog.open}
        onClose={() => setEmptyingDialog({ open: false, container: null, containerIndex: null, direct: false, source: 'containers' })}
        container={emptyingDialog.container}
        containerIndex={emptyingDialog.containerIndex}
        showQuality={['operation', 'teardown'].includes(effectivePhase)}
        onConfirm={emptyingDialog.direct ? confirmDirectEmpty : confirmEmptying}
        direct={!!emptyingDialog.direct}
        loading={loading}
      />
      <div 
        className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
        data-testid="point-detail-overlay"
        style={{ touchAction: 'none' }}
      >
        <div 
          className="bg-zinc-900 border border-zinc-700 shadow-2xl w-full sm:w-auto sm:max-w-lg overflow-hidden flex flex-col rounded-t-xl sm:rounded-xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="point-detail-dialog"
          style={{ maxHeight: '85vh', fontSize: '16px' }}
        >
        {/* Header */}
        <div className="p-4 border-b border-zinc-700 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="h-6 w-6 text-primary flex-shrink-0" />
              <h2 className="text-2xl font-bold font-heading uppercase truncate" data-testid="point-name">
                {point.name}
              </h2>
              {point.qr_ref && (
                <span className="text-xs font-mono font-bold text-primary bg-primary/10 border border-primary/30 rounded px-2 py-0.5 flex-shrink-0" data-testid="point-qr-ref" title="QR-referencenummer (genbrugeligt)">
                  {point.qr_ref}
                </span>
              )}
            </div>
            <p className="text-base text-zinc-400 font-mono">
              {POINT_TYPE_LABELS[point.point_type] || point.point_type}
            </p>
            {/* Opmærksomhedsmarkører */}
            {(point.marker_ids || []).length > 0 && attentionMarkers.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="point-attention-markers">
                {(point.marker_ids || []).map(mid => {
                  const m = attentionMarkers.find(x => x.id === mid);
                  if (!m) return null;
                  return (
                    <span
                      key={mid}
                      className="inline-flex items-center gap-1.5 text-xs font-mono px-2 py-1 rounded border"
                      style={{ borderColor: m.color, color: m.color, background: `${m.color}15` }}
                      data-testid={`point-marker-${mid}`}
                    >
                      <span
                        aria-hidden
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: m.symbol === 'dot' ? '50%' : '0',
                          ...(m.symbol === 'dot' ? { background: m.color } : {}),
                          ...(m.symbol === 'circle' ? { borderRadius: '50%', border: `2px solid ${m.color}` } : {}),
                          ...(m.symbol === 'triangle' ? {
                            width: 0, height: 0,
                            borderLeft: '5px solid transparent',
                            borderRight: '5px solid transparent',
                            borderBottom: `9px solid ${m.color}`,
                            background: 'transparent'
                          } : {}),
                        }}
                      />
                      {m.name}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="flex-shrink-0 hover:bg-zinc-800 h-10 w-10"
            data-testid="close-dialog-btn"
          >
            <X className="h-6 w-6" />
          </Button>
        </div>

        {/* Status Bar - skjules for opmærksomhedspunkter, drikkevand, pausetelt, food og drinks */}
        {!['opmærksomhedspunkt', 'drikkevand', 'pausetelt', 'snack', 'kaffe_te', 'food', 'drinks'].includes(point.point_type) && (
          <div className="px-4 py-3 bg-zinc-800/50 border-b border-zinc-700">
            <div className="flex items-center justify-between">
              <span className="text-base font-mono text-zinc-400">
                {isSetupOrTeardown ? (isSetupMode ? 'Opbygningsstatus:' : 'Nedpakningsstatus:') : 'Nuværende status:'}
              </span>
              <div className="flex items-center gap-2">
                {isSetupOrTeardown && setupStatusConfig ? (
                  <>
                    <span className={`w-4 h-4 rounded-full ${setupStatusConfig.color}`}></span>
                    <span className={`text-base font-bold ${setupStatusConfig.textColor}`}>
                      {setupStatusConfig.text}
                    </span>
                  </>
                ) : (
                  <>
                    <span className={`w-4 h-4 rounded-full ${currentStatus.color}`}></span>
                    <span className={`text-base font-bold ${currentStatus.textColor}`}>
                      {currentStatus.text}
                    </span>
                  </>
                )}
              </div>
            </div>
            {isSetupOrTeardown && (
              <div className="mt-2 text-sm text-zinc-500 font-mono">
                {isSetupMode ? '📦 Opbygning - afkryds når opsat' : '🏠 Nedpakning - afkryds når hjemtaget'}
              </div>
            )}
          </div>
        )}

        {/* "Sæt i drift" — kun admin, kun i opbygning/nedtagning, kun ressource-øer */}
        {realIsSetupOrTeardown && isAdmin && point.point_type === 'ressourceø' && (
          <div
            className="px-4 py-3 border-b border-zinc-700 flex items-center justify-between gap-3"
            style={{ background: point.operational ? 'rgba(20,184,166,0.12)' : 'rgba(168,85,247,0.06)' }}
            data-testid="operational-banner"
          >
            <div className="min-w-0">
              <p className="text-sm font-bold text-white flex items-center gap-2">
                <span className="w-3 h-3 rounded-full inline-block" style={{ background: point.operational ? '#14b8a6' : '#71717a' }}></span>
                {point.operational ? 'I drift (turkis)' : 'Ikke i drift'}
              </p>
              <p className="text-xs text-zinc-400 mt-0.5">
                {point.operational
                  ? `Tømning kan bestilles selvom vi er i ${currentPhase === 'teardown' ? 'nedtagning' : 'opbygning'}. Lilla = tømning bestilt.`
                  : 'Sæt i drift for at kunne bestille tømning på en etableret ø nu.'}
              </p>
            </div>
            <Button
              size="sm"
              onClick={toggleOperational}
              disabled={loading}
              data-testid="toggle-operational-btn"
              className={point.operational ? 'bg-zinc-700 hover:bg-zinc-600 text-white shrink-0' : 'bg-teal-600 hover:bg-teal-500 text-white shrink-0'}
            >
              {point.operational ? 'Tag ud af drift' : 'Sæt i drift'}
            </Button>
          </div>
        )}


        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          
          {/* ===== OPBYGNING/NEDPAKNING VIEW ===== */}
          {isSetupOrTeardown && (
            <div className="space-y-4">
              <h3 className="text-sm font-bold font-mono uppercase text-zinc-300 flex items-center gap-2">
                {isSetupMode ? '📦 Opsat' : '🏠 Hjemtaget'} 
                <span className="text-xs text-zinc-500 font-normal">
                  - afkryds hvad der er {isSetupMode ? 'opsat' : 'hjemtaget'}
                </span>
              </h3>
              
              {/* For ressourceø: Vis containere der skal opsættes - med antal-vælger */}
              {point.point_type === 'ressourceø' && point.containers?.length > 0 && (
                <div className="space-y-2">
                  {point.containers.map((container, idx) => {
                    const containerId = `${container.container_size}_${container.fraction_type}_${idx}`;
                    const totalCount = container.count || 1;
                    // Brug lokal checklist for live-opdatering
                    const checkedCount = localChecklist[containerId] ?? 0;
                    const isPartial = checkedCount > 0 && checkedCount < totalCount;
                    const isComplete = checkedCount >= totalCount;
                    
                    return (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg transition-all ${
                          isComplete 
                            ? 'bg-green-500/20 border border-green-500/50' 
                            : isPartial
                              ? 'bg-yellow-500/20 border border-yellow-500/50'
                              : 'bg-zinc-800/50 border border-zinc-700'
                        }`}
                        data-testid={`setup-container-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className={`font-bold text-sm ${
                              isComplete ? 'text-green-400' : isPartial ? 'text-yellow-400' : 'text-white'
                            }`}>
                              {totalCount}x {container.container_size}
                            </p>
                            <p className="text-sm text-white font-mono font-semibold">
                              {container.fraction_type}
                            </p>
                          </div>
                          
                          {/* Antal-vælger med +/- knapper */}
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0 bg-zinc-800 border-zinc-600 hover:bg-zinc-700"
                              onClick={() => handleSetupCheck(containerId, checkedCount - 1, totalCount)}
                              disabled={loading || checkedCount <= 0}
                              data-testid={`container-${idx}-minus`}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className={`w-12 text-center font-bold ${
                              isComplete ? 'text-green-400' : isPartial ? 'text-yellow-400' : 'text-zinc-400'
                            }`}>
                              {checkedCount}/{totalCount}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0 bg-zinc-800 border-zinc-600 hover:bg-zinc-700"
                              onClick={() => handleSetupCheck(containerId, Math.min(totalCount, checkedCount + 1), totalCount)}
                              disabled={loading || checkedCount >= totalCount}
                              data-testid={`container-${idx}-plus`}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          {isComplete && (
                            <span className="text-green-500 text-xl ml-2">✓</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* For ressourcespand MED bins: Vis antal-vælger for hver bin */}
              {point.point_type === 'ressourcespand' && point.bins?.length > 0 && (
                <div className="space-y-2">
                  {point.bins.map((bin, idx) => {
                    const binId = `bin_${bin.fraction_type}_${idx}`;
                    const totalCount = bin.count || 1;
                    // Brug lokal checklist for live-opdatering
                    const checkedCount = localChecklist[binId] ?? 0;
                    const isPartial = checkedCount > 0 && checkedCount < totalCount;
                    const isComplete = checkedCount >= totalCount;
                    
                    return (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg transition-all ${
                          isComplete 
                            ? 'bg-green-500/20 border border-green-500/50' 
                            : isPartial
                              ? 'bg-yellow-500/20 border border-yellow-500/50'
                              : 'bg-zinc-800/50 border border-zinc-700'
                        }`}
                        data-testid={`setup-bin-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className={`font-bold text-sm ${
                              isComplete ? 'text-green-400' : isPartial ? 'text-yellow-400' : 'text-white'
                            }`}>
                              {bin.count}x {bin.container_size || 'Spand'}
                            </p>
                            <p className="text-sm text-white font-mono font-semibold">
                              {RESOURCE_BIN_FRACTIONS[bin.fraction_type] || bin.fraction_type}
                            </p>
                          </div>
                          
                          {/* Antal-vælger med +/- knapper */}
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0 bg-zinc-800 border-zinc-600 hover:bg-zinc-700"
                              onClick={() => handleSetupCheck(binId, checkedCount - 1, totalCount)}
                              disabled={loading || checkedCount <= 0}
                              data-testid={`bin-${idx}-minus`}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className={`w-12 text-center font-bold ${
                              isComplete ? 'text-green-400' : isPartial ? 'text-yellow-400' : 'text-zinc-400'
                            }`}>
                              {checkedCount}/{totalCount}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0 bg-zinc-800 border-zinc-600 hover:bg-zinc-700"
                              onClick={() => handleSetupCheck(binId, checkedCount + 1, totalCount)}
                              disabled={loading || checkedCount >= totalCount}
                              data-testid={`bin-${idx}-plus`}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          {isComplete && (
                            <span className="text-green-500 text-xl ml-2">✓</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* For ressourcespand med GAMMEL struktur (bin_fraction) - vis også som antal-vælger */}
              {point.point_type === 'ressourcespand' && point.bin_fraction && (!point.bins || point.bins.length === 0) && (
                <div className="space-y-2">
                  {(() => {
                    const binId = `bin_${point.bin_fraction}_0`;
                    const totalCount = 1;
                    // Brug lokal checklist
                    const checkedCount = localChecklist[binId] ?? 0;
                    const isComplete = checkedCount >= totalCount;
                    
                    return (
                      <div 
                        className={`p-3 rounded-lg transition-all ${
                          isComplete 
                            ? 'bg-green-500/20 border border-green-500/50' 
                            : 'bg-zinc-800/50 border border-zinc-700'
                        }`}
                        data-testid="setup-bin-legacy"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className={`font-bold text-sm ${isComplete ? 'text-green-400' : 'text-white'}`}>
                              1x {RESOURCE_BIN_FRACTIONS[point.bin_fraction] || point.bin_fraction}
                            </p>
                            <p className="text-xs text-zinc-400 font-mono">
                              Spand
                            </p>
                          </div>
                          
                          {/* Simpel toggle for single item */}
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className={`px-3 ${
                                isComplete 
                                  ? 'bg-green-500/30 border-green-500 text-green-400' 
                                  : 'bg-zinc-800 border-zinc-600 hover:bg-zinc-700'
                              }`}
                              onClick={() => handleSetupCheck(binId, isComplete ? 0 : 1, 1)}
                              disabled={loading}
                              data-testid="bin-legacy-toggle"
                            >
                              {isComplete ? '✓ Opsat' : 'Markér opsat'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              
              {/* For punkter UDEN containere/bins: Opmærksomhedspunkt med billede */}
              {point.point_type === 'opmærksomhedspunkt' && (
                <AttentionPointSection 
                  point={point} 
                  onUpdate={onUpdate}
                  onDelete={onDelete}
                  token={token}
                />
              )}
              
              {/* Drikkevand og Pausetelt - simple punkter med beskrivelse */}
              {(point.point_type === 'drikkevand' || point.point_type === 'pausetelt') && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-zinc-300">
                    <span className="text-2xl">
                      {point.point_type === 'drikkevand' ? '💧' : '⛺'}
                    </span>
                    <div>
                      <p className="text-sm font-mono uppercase text-zinc-400">
                        {point.point_type === 'drikkevand' ? 'Drikkevand' : 'Pausetelt'}
                      </p>
                      <p className="text-white mt-1">
                        {point.point_type === 'drikkevand' 
                          ? 'Her kan du fylde din drikkedunk' 
                          : 'Her kan du holde en kort pause'}
                      </p>
                      {point.description && (
                        <p className="text-zinc-400 text-sm mt-2 italic">{point.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* ===== NORMAL DRIFT (Afvikling/Planlægning) ===== */}
          {!isSetupOrTeardown && (
            <>
              {/* Drikkevand og Pausetelt - simple punkter med beskrivelse (NORMAL DRIFT) */}
              {(point.point_type === 'drikkevand' || point.point_type === 'pausetelt') && (
                <div className="space-y-3">
                  <div className="flex items-center gap-4 text-zinc-300">
                    <span className="text-3xl">
                      {point.point_type === 'drikkevand' ? '💧' : '⛺'}
                    </span>
                    <div>
                      <p className="text-lg font-bold text-white">
                        {point.point_type === 'drikkevand' 
                          ? 'Her kan du fylde din drikkedunk' 
                          : 'Her kan du holde en kort pause'}
                      </p>
                      {point.description && (
                        <p className="text-zinc-400 text-sm mt-1">{point.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
          {/* ===== CHAUFFØR VIEW ===== */}
          {isDriver && (
            <>
              {/* Vis kun containere der venter på tømning */}
              {point.point_type === 'ressourceø' && (
                <div className="space-y-3">
                  <h3 className="text-sm font-bold font-mono uppercase text-zinc-300 flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Containere der skal tømmes
                  </h3>
                  
                  {hasPendingRequests ? (
                    <div className="space-y-2">
                      {point.containers?.filter(isContainerPending).map((container, idx) => {
                        const pcount = pendingContainers
                          .filter(pc => pc.container_size === container.container_size && pc.fraction_type === container.fraction_type)
                          .reduce((s, pc) => s + (pc.count || 1), 0);
                        return (
                        <div 
                          key={idx}
                          className="p-3 rounded-lg bg-red-500/10 border border-red-500/30"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-red-500">🔴</span>
                            <div className="flex-1">
                              <p className="font-bold text-sm">
                                {container.container_size}
                              </p>
                              <p className="text-xs text-zinc-400 font-mono">
                                {container.fraction_type}
                              </p>
                            </div>
                            {pcount > 1 && (
                              <span className="text-lg font-bold text-red-400 font-mono" data-testid={`pending-count-${idx}`}>×{pcount}</span>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-zinc-500 italic">
                      Ingen containere venter på tømning
                    </p>
                  )}

                  {/* Andre containere: registrér direkte som tømt uden bestilling */}
                  {point.containers?.some(c => !isContainerPending(c)) && (
                    <div className="pt-3 border-t border-zinc-700 space-y-2">
                      <h3 className="text-sm font-bold font-mono uppercase text-zinc-300">
                        Andre containere
                      </h3>
                      <p className="text-xs text-zinc-500">
                        Tømmer du en container uden bestilling? Registrér den her.
                      </p>
                      {point.containers.map((container, cIdx) => {
                        if (isContainerPending(container)) return null;
                        return (
                          <div 
                            key={`other-${cIdx}`}
                            className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700 flex items-center gap-3"
                          >
                            <div className="flex-1">
                              <p className="font-bold text-sm">
                                {container.count}x {container.container_size}
                              </p>
                              <p className="text-xs text-zinc-400 font-mono">
                                {container.fraction_type}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20 text-emerald-400"
                              onClick={() => setEmptyingDialog({ open: true, container: container, containerIndex: cIdx, direct: true })}
                              disabled={loading}
                              data-testid={`driver-direct-empty-${cIdx}`}
                            >
                              ✓ Registrér som tømt
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Stor "Tømning er på vej" knap for chauffør */}
              {hasPendingRequests && point.status !== 'gul' && (
                <Button
                  className="w-full h-16 text-lg bg-yellow-500 hover:bg-yellow-600 text-black font-bold uppercase"
                  onClick={() => handlePointStatusChange('gul')}
                  disabled={loading}
                  data-testid="driver-on-way-btn"
                >
                  <Truck className="h-6 w-6 mr-3" />
                  Tømning er på vej
                </Button>
              )}

              {point.status === 'gul' && (
                <div className="p-4 bg-yellow-500/20 border border-yellow-500/50 rounded-lg text-center">
                  <Truck className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
                  <p className="text-yellow-400 font-bold">Du er på vej</p>
                  <p className="text-sm text-zinc-400 mt-1">
                    Scan QR-koden ved ressourceøen for at markere containere som tømt
                  </p>
                </div>
              )}

              {!hasPendingRequests && (
                <div className="p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-center">
                  <p className="text-green-400 font-bold">✓ Alt er tømt</p>
                  <p className="text-sm text-zinc-400 mt-1">
                    Ingen ventende tømningsanmodninger
                  </p>
                </div>
              )}
            </>
          )}

          {/* ===== RESSOURCESPAND VIEW (for alle) - afvikling ===== */}
          {point.point_type === 'ressourcespand' && (
            <div className="space-y-4">
              {/* Spande liste (bins) - klikbar bin-for-bin (samme flow som ressourceø) */}
              {point.bins && point.bins.length > 0 ? (
                <div className="space-y-3">
                  <h3 className="text-sm font-bold font-mono uppercase text-zinc-300 flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Spande ({point.bins.length})
                  </h3>
                  <div className="space-y-2">
                    {point.bins.map((bin, idx) => {
                      const binStatus = getBinStatus(bin);
                      const isSelected = selectedBin === idx;
                      return (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg border transition-all cursor-pointer ${
                            binStatus === 'rød'
                              ? 'bg-red-500/10 border-red-500/30'
                              : binStatus === 'gul'
                                ? 'bg-yellow-500/10 border-yellow-500/30'
                                : isSelected
                                  ? 'bg-green-500/10 border-green-500/30'
                                  : 'bg-blue-500/10 border-blue-500/30 hover:border-blue-400'
                          }`}
                          onClick={() => setSelectedBin(isSelected ? null : idx)}
                          data-testid={`bin-item-${idx}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className={
                                binStatus === 'rød' ? 'text-red-500' :
                                binStatus === 'gul' ? 'text-yellow-500' :
                                'text-green-500'
                              }>
                                {binStatus === 'rød' ? '🔴' : binStatus === 'gul' ? '🟡' : '🟢'}
                              </span>
                              <div>
                                <p className="font-bold text-sm text-white">
                                  {bin.count}x {bin.container_size || 'Spand'}
                                </p>
                                <p className="text-sm text-white font-mono font-semibold">
                                  {RESOURCE_BIN_FRACTIONS[bin.fraction_type] || bin.fraction_type}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              {binStatus === 'rød' ? (
                                <span className="text-xs text-red-400 font-medium">Venter på tømning</span>
                              ) : binStatus === 'gul' ? (
                                <span className="text-xs text-yellow-400 font-medium">Tømning på vej</span>
                              ) : (
                                <span className="text-xs text-green-400 font-medium">OK</span>
                              )}
                              <p className="text-[10px] text-zinc-500">Klik for handlinger</p>
                            </div>
                          </div>

                          {/* Handlinger når spanden er valgt */}
                          {isSelected && (canChangeStatusRessourcespande || canOrderEmptyingRessourcespande) && (
                            <div className="mt-3 pt-3 border-t border-zinc-700 space-y-2">
                              <p className="text-xs text-zinc-400 mb-2">
                                Status: <span className={
                                  binStatus === 'rød' ? 'text-red-400' :
                                  binStatus === 'gul' ? 'text-yellow-400' :
                                  'text-green-400'
                                }>
                                  {binStatus === 'rød' ? 'Venter på tømning' :
                                   binStatus === 'gul' ? 'Tømning på vej' :
                                   'OK / Tømt'}
                                </span>
                              </p>
                              <div className="flex flex-col gap-2">
                                {binStatus === 'grøn' && (
                                  <div className="grid grid-cols-2 gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className={`text-xs h-auto py-4 flex-col gap-1 bg-red-500/10 border-red-500/30 hover:bg-red-500/20 text-red-400 ${!canChangeStatusRessourcespande ? 'col-span-2' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleBinStatusChange(idx, 'rød');
                                      }}
                                      disabled={loading || !canOrderEmptyingRessourcespande}
                                      title={!canOrderEmptyingRessourcespande ? 'Du har ikke rettighed til at bestille tømning' : ''}
                                      data-testid={`bin-${idx}-order-btn`}
                                    >
                                      <span className="text-xl">🔴</span>
                                      <span>Bestil tømning</span>
                                    </Button>
                                    {canChangeStatusRessourcespande && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-xs h-auto py-4 flex-col gap-1 bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20 text-emerald-400"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEmptyingDialog({ open: true, container: bin, containerIndex: idx, direct: true, source: 'bins' });
                                        }}
                                        disabled={loading}
                                        data-testid={`bin-${idx}-direct-empty-btn`}
                                      >
                                        <span className="text-xl">✓</span>
                                        <span>Registrér som tømt nu</span>
                                      </Button>
                                    )}
                                  </div>
                                )}
                                {binStatus === 'rød' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-xs w-full bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20 text-yellow-400"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleBinStatusChange(idx, 'gul');
                                    }}
                                    disabled={loading || !canChangeStatusRessourcespande}
                                    title={!canChangeStatusRessourcespande ? 'Du har ikke rettighed til at ændre status' : ''}
                                    data-testid={`bin-${idx}-progress-btn`}
                                  >
                                    🟡 Tømning er på vej
                                  </Button>
                                )}
                                {binStatus === 'gul' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-xs w-full bg-green-500/10 border-green-500/30 hover:bg-green-500/20 text-green-400"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleBinStatusChange(idx, 'grøn');
                                    }}
                                    disabled={loading || !canChangeStatusRessourcespande}
                                    title={!canChangeStatusRessourcespande ? 'Du har ikke rettighed til at markere som tømt' : ''}
                                    data-testid={`bin-${idx}-done-btn`}
                                  >
                                    🟢 Marker som tømt
                                  </Button>
                                )}
                                <p className="text-[10px] text-zinc-500 text-center">
                                  Flow: Bestil → På vej → Tømt (eller Registrér direkte)
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : point.bin_fraction ? (
                /* Bagudkompatibilitet: Gammel struktur med enkelt bin_fraction */
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500/30 border-2 border-blue-500 flex items-center justify-center">
                      <span className="text-blue-400 text-sm">🗑️</span>
                    </div>
                    <div>
                      <p className="font-bold text-white">
                        1x {RESOURCE_BIN_FRACTIONS[point.bin_fraction] || point.bin_fraction}
                      </p>
                      <p className="text-xs text-zinc-400 font-mono">
                        Ressourcespand • {point.bin_fraction === 'stativ' ? 'Stativ' : 'Publikumsområde'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg text-center text-zinc-500">
                  Ingen spande tilføjet
                </div>
              )}
            </div>
          )}
            </>
          )}
          {/* ===== END OF NORMAL DRIFT ===== */}

          {/* Description - vises for alle brugere der kan se punktet */}
          {point.description && (
            <div className="text-sm text-zinc-400 bg-zinc-800/30 p-3 rounded-lg">
              {point.description}
            </div>
          )}

          {/* Containers (for ressourceø) - vises KUN i normal drift (afvikling), IKKE i opbygning/nedpakning */}
          {!isSetupOrTeardown && point.point_type === 'ressourceø' && point.containers && point.containers.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-bold font-mono uppercase text-zinc-300 flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Containere ({point.containers.length})
                  </h3>
                  <div className="space-y-2">
                    {point.containers.map((container, idx) => {
                      const isPending = isContainerPending(container);
                      const containerStatus = getContainerStatus(container);
                      return (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border transition-all ${
                            !isSetupOrTeardown ? 'cursor-pointer' : ''
                          } ${
                            isPending && !isSetupOrTeardown
                              ? 'bg-red-500/10 border-red-500/30' 
                              : selectedContainer === idx && !isSetupOrTeardown
                                ? 'bg-green-500/10 border-green-500/30' 
                                : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-500'
                          }`}
                          onClick={() => !isSetupOrTeardown && setSelectedContainer(selectedContainer === idx ? null : idx)}
                          data-testid={`container-${idx}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {/* Status indikator - kun i afvikling */}
                              {!isSetupOrTeardown && (
                                <span className={
                                  containerStatus === 'rød' ? 'text-red-500' : 
                                  containerStatus === 'gul' ? 'text-yellow-500' : 
                                  'text-green-500'
                                }>
                                  {containerStatus === 'rød' ? '🔴' : containerStatus === 'gul' ? '🟡' : '🟢'}
                                </span>
                              )}
                              <div>
                                <p className="font-bold text-sm">
                                  {container.count}x {container.container_size}
                                </p>
                                <p className="text-sm text-white font-mono font-semibold">
                                  {container.fraction_type}
                                </p>
                              </div>
                            </div>
                            {/* Status tekst - kun i afvikling */}
                            {!isSetupOrTeardown && (
                              <div className="text-right">
                                {containerStatus === 'rød' ? (
                                  <span className="text-xs text-red-400 font-medium">Venter på tømning</span>
                                ) : containerStatus === 'gul' ? (
                                  <span className="text-xs text-yellow-400 font-medium">Tømning på vej</span>
                                ) : (
                                  <span className="text-xs text-green-400 font-medium">OK</span>
                                )}
                                <p className="text-[10px] text-zinc-500">Klik for handlinger</p>
                              </div>
                            )}
                          </div>
                          
                          {/* Container actions when selected - KUN I AFVIKLING */}
                          {!isSetupOrTeardown && selectedContainer === idx && (
                            <div className="mt-3 pt-3 border-t border-zinc-700 space-y-2">
                              <p className="text-xs text-zinc-400 mb-2">
                                Status: <span className={
                                  containerStatus === 'rød' ? 'text-red-400' : 
                                  containerStatus === 'gul' ? 'text-yellow-400' : 
                                  'text-green-400'
                                }>
                                  {containerStatus === 'rød' ? 'Venter på tømning' : 
                                   containerStatus === 'gul' ? 'Tømning på vej' : 
                                   'OK / Tømt'}
                                </span>
                              </p>
                              <div className="flex flex-col gap-2">
                                {/* Tvunget flow: Grøn → Bestil → På vej → Tømt */}
                                {containerStatus === 'grøn' && (
                                <div className="grid grid-cols-2 gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className={`text-xs h-auto py-4 flex-col gap-1 bg-red-500/10 border-red-500/30 hover:bg-red-500/20 text-red-400 ${!canChangeStatusForPointType() ? 'col-span-2' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleContainerStatusChange(idx, 'rød');
                                  }}
                                  disabled={loading || !canOrderForPointType()}
                                  title={!canOrderForPointType() ? 'Du har ikke rettighed til at bestille tømning' : ''}
                                  data-testid={`container-${idx}-order-btn`}
                                >
                                  <span className="text-xl">🔴</span>
                                  <span>Bestil tømning</span>
                                </Button>
                                {canChangeStatusForPointType() && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs h-auto py-4 flex-col gap-1 bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20 text-emerald-400"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEmptyingDialog({ open: true, container: container, containerIndex: idx, direct: true });
                                  }}
                                  disabled={loading}
                                  data-testid={`container-${idx}-direct-empty-btn`}
                                >
                                  <span className="text-xl">✓</span>
                                  <span>Registrér som tømt nu</span>
                                </Button>
                                )}
                                </div>
                                )}
                                {containerStatus === 'rød' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs w-full bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20 text-yellow-400"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleContainerStatusChange(idx, 'gul');
                                  }}
                                  disabled={loading || !canChangeStatusForPointType()}
                                  title={!canChangeStatusForPointType() ? 'Du har ikke rettighed til at ændre status' : ''}
                                  data-testid={`container-${idx}-progress-btn`}
                                >
                                  🟡 Tømning er på vej
                                </Button>
                                )}
                                {containerStatus === 'gul' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs w-full bg-green-500/10 border-green-500/30 hover:bg-green-500/20 text-green-400"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleContainerStatusChange(idx, 'grøn');
                                  }}
                                  disabled={loading || !canChangeStatusForPointType()}
                                  title={!canChangeStatusForPointType() ? 'Du har ikke rettighed til at markere som tømt' : ''}
                                  data-testid={`container-${idx}-done-btn`}
                                >
                                  🟢 Marker som tømt
                                </Button>
                                )}
                                <p className="text-[10px] text-zinc-500 text-center">
                                  Flow: Bestil → På vej → Tømt
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Global Status Actions - FJERNET for ressourceø og ressourcespand (brug container/bin-level i stedet)
                  Beholdes kun for punkttyper UDEN container-liste (bl.a. maxicontainer) */}
              {!isSetupOrTeardown && 
               !['drikkevand', 'pausetelt', 'snack', 'kaffe_te', 'opmærksomhedspunkt', 'ressourceø', 'ressourcespand'].includes(point.point_type) && 
               canChangeStatusForPointType() && (
              <div className="space-y-3">
                <h3 className="text-sm font-bold font-mono uppercase text-zinc-300">
                  Skift status for hele punktet
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <Button
                    variant="outline"
                    className={`flex-col h-auto py-6 gap-2 ${
                      point.status === 'rød' 
                        ? 'bg-red-500/30 border-red-500' 
                        : 'bg-red-500/10 border-red-500/30 hover:bg-red-500/20'
                    }`}
                    onClick={() => handlePointStatusChange('rød')}
                    disabled={loading || !canOrderForPointType()}
                    data-testid="status-red-btn"
                  >
                    <span className="text-4xl">🔴</span>
                    <span className="text-sm font-semibold text-red-400 text-center leading-tight">Bestil tømning</span>
                  </Button>
                  <Button
                    variant="outline"
                    className={`flex-col h-auto py-6 gap-2 ${
                      point.status === 'gul' 
                        ? 'bg-yellow-500/30 border-yellow-500' 
                        : 'bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20'
                    }`}
                    onClick={() => handlePointStatusChange('gul')}
                    disabled={loading}
                    data-testid="status-yellow-btn"
                  >
                    <span className="text-4xl">🟡</span>
                    <span className="text-sm font-semibold text-yellow-400 text-center leading-tight">Tømning på vej</span>
                  </Button>
                  <Button
                    variant="outline"
                    className={`flex-col h-auto py-6 gap-2 ${
                      point.status === 'grøn' 
                        ? 'bg-green-500/30 border-green-500' 
                        : 'bg-green-500/10 border-green-500/30 hover:bg-green-500/20'
                    }`}
                    onClick={() => handlePointStatusChange('grøn')}
                    disabled={loading}
                    data-testid="status-green-btn"
                  >
                    <span className="text-4xl">🟢</span>
                    <span className="text-sm font-semibold text-green-400 text-center leading-tight">Tømt / OK</span>
                  </Button>
                </div>
              </div>
              )}

              {/* Notes Section - for food, drinks og ressourceø */}
              {showNotesSection && (
                <div className="space-y-3" data-testid="point-notes-section">
                  <h3 className="text-sm font-bold font-mono uppercase text-zinc-300 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Noter ({pointNotes.length})
                  </h3>
                  
                  {/* Add note */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                      placeholder="Skriv en note..."
                      className="flex-1 px-3 py-2 bg-zinc-700 border border-zinc-600 rounded text-sm text-white placeholder-zinc-400"
                      data-testid="point-note-input"
                    />
                    <Button 
                      size="sm" 
                      onClick={handleAddNote} 
                      disabled={!newNoteText.trim()}
                      data-testid="add-point-note-btn"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Notes list */}
                  {notesLoading ? (
                    <p className="text-xs text-zinc-400">Indlæser noter...</p>
                  ) : pointNotes.length === 0 ? (
                    <p className="text-xs text-zinc-400">Ingen noter endnu</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {pointNotes.map(note => (
                        <div key={note.id} className="bg-zinc-700/50 rounded p-2 text-sm" data-testid={`point-note-${note.id}`}>
                          <div className="flex justify-between items-start">
                            <p className="text-white whitespace-pre-wrap flex-1">{note.content}</p>
                            {isAdmin && (
                              <button 
                                onClick={() => handleDeleteNote(note.id)}
                                className="text-zinc-400 hover:text-red-400 ml-2 shrink-0"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-zinc-400 mt-1">
                            {note.user_name} - {new Date(note.created_at).toLocaleDateString('da-DK')}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-zinc-700 bg-zinc-800/50 space-y-2">
          {/* Footer vises hvis brugeren har mindst én af de handlinger, den kan udføre på punktet.
              Hver knap gates individuelt af sin egen granulære rettighed (se canEdit/canDelete). */}
          {(canEdit || canDelete) ? (
            <div className="flex gap-2">
              {canEdit && onEdit && (
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(point);
                  }}
                  data-testid="edit-point-btn"
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Rediger
                </Button>
              )}
              <Button
                variant="secondary"
                className="flex-1"
                onClick={(e) => {
                  e.stopPropagation();
                  onPrintQR?.(point);
                }}
                data-testid="print-qr-btn"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print QR
              </Button>
              {/* Kopier knap - for ressourceø, ressourcespand, food og drinks (kræver rediger-rettighed) */}
              {canEdit && ['ressourceø', 'ressourcespand', 'food', 'drinks'].includes(point.point_type) && onCopy && (
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCopy(point);
                  }}
                  data-testid="copy-point-btn"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Kopier
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="destructive"
                  onClick={(e) => {
                    console.log("Delete button clicked");
                    e.stopPropagation();
                    e.preventDefault();
                    handleDelete();
                  }}
                  disabled={loading}
                  data-testid="delete-point-btn"
                  type="button"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Slet
                </Button>
              )}
              {isAdmin && ['ressourceø', 'ressourcespand', 'maxicontainer'].includes(point.point_type) && (
                <Button
                  variant="outline"
                  onClick={async (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (!window.confirm(`Nulstil tømningsstatus for "${point.name}"?\n\nDette rydder alle gamle tømningsanmodninger (ghost-data) og sætter punktet til grøn. Brug dette hvis punktet sidder fast i en rød/gul-loop.`)) return;
                    try {
                      setLoading(true);
                      const res = await axios.post(
                        `${API}/resource-points/${point.id}/reset-emptying-state`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      toast.success(`Nulstillet: ${res.data.cleared} ghost-anmodninger ryddet`);
                      onUpdate?.();
                      onClose();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Kunne ikke nulstille');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  data-testid="reset-emptying-state-btn"
                  type="button"
                  className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                  title="Rydder gamle tømningsanmodninger og sætter punktet til grøn"
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Nulstil status
                </Button>
              )}
            </div>
          ) : null}
          <p className="text-xs text-zinc-500 text-center">
            Koordinater: {point.lat.toFixed(5)}, {point.lng.toFixed(5)}
          </p>
        </div>
      </div>
    </div>
    </>
  );
}
